import { SORT_ORDER } from '@/components/table/types';
import { useAuth } from '@/hooks/use-auth';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { useQuery } from '@apollo/client';
import { TenantType } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Tabs, { ITab, TabsLine } from '../basicComponents/tabs';
import Header from '../header';
import { LoaderOnWholeScreen } from '../loaders/loader-on-whole-screen';
import { LIST_INVESTMENTS_FOR_MANAGER_UPDATE_QUERY, PUBLISHED_INVESTMENT_MANAGER_UPDATES_INVESTMENT_IDS_QUERY } from './queries';
import { AllStrategiesTab } from './tabs/allStrategiesTab';
import { PublishedUpdatesTab } from './tabs/publishedUpdatesTab';
import { IListInvestmentsForManagerUpdateQuery } from './types';
import { ManagerUpdatesZeroState } from './zeroState';

enum ManagerUpdatesTabsEnum {
  PUBLISHED_UPDATES = 'Published Updates',
  ALL_STRATEGIES = 'All Strategies'
}

export const ManagerUpdates = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') || ManagerUpdatesTabsEnum.PUBLISHED_UPDATES;

  const [tabs, setTabs] = useState<ITab[]>([]);
  const [showZeroState, setShowZeroState] = useState(false);

  const { loading: strategiesWithUpdatesLoading } = useQuery<{ listInvestmentsForManagerUpdate: IListInvestmentsForManagerUpdateQuery }>(
    LIST_INVESTMENTS_FOR_MANAGER_UPDATE_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      skip: user.tenant.type === TenantType.ADVISORY_FIRM,
      variables: {
        sortBy: 'STRATEGY',
        order: SORT_ORDER.ASC,
        rowsPerPage: 1,
        page: 1,
        quickStatusFilter: 'Strategies with Updates'
      },
      onCompleted: (data) => {
        const { data: tableData } = data.listInvestmentsForManagerUpdate;
        if (!tableData.length) {
          setShowZeroState(true);
        }
      },
      onError: (error) => {
        console.error(error);
      }
    }
  );

  const { data: publishedInvestmentManagerUpdatesInvestmentIdsData, loading: publishedInvestmentManagerUpdatesInvestmentIdsLoading } = useQuery(
    PUBLISHED_INVESTMENT_MANAGER_UPDATES_INVESTMENT_IDS_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      skip: user?.tenant?.type === TenantType.ADVISORY_FIRM,
      variables: {
        data: {
          publishedUpdatesOnly: true
        }
      }
    }
  );

  useEffect(() => {
    if (publishedInvestmentManagerUpdatesInvestmentIdsLoading) return;

    if (user?.tenant?.type === TenantType.PLATFORM_MANAGER) {
      if (publishedInvestmentManagerUpdatesInvestmentIdsData?.publishedInvestmentManagerUpdates?.length > 0) {
        setTabs([
          { value: ManagerUpdatesTabsEnum.PUBLISHED_UPDATES, amount: null },
          { value: ManagerUpdatesTabsEnum.ALL_STRATEGIES, amount: null }
        ]);
      } else {
        onChangeActiveTab(ManagerUpdatesTabsEnum.ALL_STRATEGIES);
      }
      return;
    }

    onChangeActiveTab(ManagerUpdatesTabsEnum.PUBLISHED_UPDATES);
  }, [user, activeTab, publishedInvestmentManagerUpdatesInvestmentIdsLoading]);

  const onChangeActiveTab = (value: string) => {
    if (value === activeTab) return;
    setSearchParams({ tab: value }, { replace: true });
  };

  const onViewAllStrategiesButtonClick = () => {
    onChangeActiveTab(ManagerUpdatesTabsEnum.ALL_STRATEGIES);
    setShowZeroState(false);
  };

  if (strategiesWithUpdatesLoading || publishedInvestmentManagerUpdatesInvestmentIdsLoading) {
    return <LoaderOnWholeScreen />;
  }

  if (showZeroState) {
    return <ManagerUpdatesZeroState onViewAllStrategiesButtonClick={onViewAllStrategiesButtonClick} />;
  }

  return (
    <>
      <MainWrap>
        <Header />
        {tabs?.length > 0 && (
          <>
            <PaddingWrap>
              <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={(tab) => onChangeActiveTab(tab)} />
            </PaddingWrap>
            <TabsLine />
          </>
        )}
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          {activeTab === ManagerUpdatesTabsEnum.PUBLISHED_UPDATES && <PublishedUpdatesTab />}
          {activeTab === ManagerUpdatesTabsEnum.ALL_STRATEGIES && <AllStrategiesTab />}
        </PaddingWrap>
      </MainWrap>
    </>
  );
};
